/*
 * /tooth-shade — замер оттенка зубов по фотографии.
 *
 * Что известно из литературы и что определило устройство тула:
 * подбор оттенка по фотографии смартфона БЕЗ калибровки попадает примерно в
 * 38 % случаев. То есть «сфотографируйте зубы, и мы скажем ваш оттенок» —
 * это подбрасывание монетки с научным видом.
 *
 * Отсюда три решения, каждое против удобства и за правду:
 *
 *  1. БЕЗ ЭТАЛОНА БЕЛОГО НЕ МЕРИМ. В кадре обязан быть белый лист бумаги.
 *     По нему считается баланс белого, и только после этого цвет зуба
 *     что-то значит.
 *
 *  2. ПЕРЕСВЕЧЕННЫЙ ЭТАЛОН — ОТКАЗ. Если бумага выбита в 255, информации о
 *     балансе в ней НЕТ: любой свет, будучи ярче потолка шкалы, выглядит
 *     одинаково. Прибор, упёршийся в потолок, слеп к собственной ошибке, и
 *     единственный честный ответ здесь — «переснимите темнее».
 *
 *  3. ГЛАВНАЯ ВЕЛИЧИНА — НЕ ОТТЕНОК, А СДВИГ. Абсолютный оттенок по фото
 *     всегда спорен; РАЗНИЦА между двумя своими снимками, снятыми одинаково
 *     и приведёнными к одному эталону, надёжна. Поэтому тул хранит замеры и
 *     считает, на сколько ступеней шкалы вы сдвинулись за курс отбеливания.
 */

import { shadeCardBlob } from './shade-card.js';

const CFG = window.__TOOTH_SHADE || {};
const T = CFG.i18n || {};
const $ = s => document.querySelector(s);
const $$ = s => Array.from(document.querySelectorAll(s));

const KEY = 'tooth-shade:log';

const state = {
    img: null,          // ImageBitmap
    canvas: null,
    ctx: null,
    white: null,        // { r,g,b } средний по образцу бумаги
    tooth: null,        // { r,g,b }
    picking: 'white',
    log: [],            // [{ at, shade, index, L, note }]
};

// ── запуск ──────────────────────────────────────────────────────────────

function boot() {
    state.canvas = $('#tsCanvas');
    state.ctx = state.canvas.getContext('2d', { willReadFrequently: true });

    const up = $('#tsUpload');
    if (window.__bindUploaderFile) window.__bindUploaderFile(up, onFile);
    else up.addEventListener('tool-uploader:file', ev => {
        const f = ev.detail?.files?.length ? ev.detail.files[0] : ev.detail?.file;
        if (f) onFile(f);
    });

    state.canvas.addEventListener('click', onCanvasClick);
    $$('[data-pick]').forEach(b => b.addEventListener('click', () => setPicking(b.dataset.pick)));
    $('#tsSave').addEventListener('click', saveMeasure);
    $('#tsClear').addEventListener('click', clearLog);

    /*
     * Карточка выдаётся ДО загрузки фото и живёт рядом с инструкцией по
     * съёмке: она нужна человеку в тот момент, когда он ещё только идёт
     * фотографироваться, а не когда снимок уже сделан не так.
     */
    const card = $('#tsCard');
    if (card) {
        card.addEventListener('click', async () => {
            card.disabled = true;
            try {
                const blob = await shadeCardBlob(T, 300);
                window.__downloadFile(blob, 'timbrica-shade-card.png');
                if (window.__aeFunnel) window.__aeFunnel('card_downloaded');
            } catch (e) {
                window.__handleError && window.__handleError(e, 'shade-card');
            } finally {
                card.disabled = false;
            }
        });
    }

    restore();
    renderLog();
    if (window.__stage) window.__stage('ready');
}

async function onFile(file) {
    try {
        if (window.__stage) window.__stage('file_chosen', { mb: Math.round((file.size || 0) / 1048576) });
        const bmp = await createImageBitmap(file);
        state.img = bmp;
        state.white = null;
        state.tooth = null;
        setPicking('white');
        drawImage();
        $('#tsWork').hidden = false;
        renderResult();
    } catch (e) {
        window.__handleError(e, 'tooth-shade:open');
    }
}

function drawImage() {
    const maxW = 900;
    const scale = Math.min(1, maxW / state.img.width);
    state.canvas.width = Math.round(state.img.width * scale);
    state.canvas.height = Math.round(state.img.height * scale);
    state.ctx.drawImage(state.img, 0, 0, state.canvas.width, state.canvas.height);
    drawMarks();
}

function drawMarks() {
    for (const [key, mark] of [['white', state.whiteAt], ['tooth', state.toothAt]]) {
        if (!mark) continue;
        const c = state.ctx;
        c.save();
        c.strokeStyle = key === 'white' ? '#38bdf8' : '#f59e0b';
        c.lineWidth = 3;
        c.strokeRect(mark.x - 14, mark.y - 14, 28, 28);
        c.restore();
    }
}

function setPicking(what) {
    state.picking = what;
    $$('[data-pick]').forEach(b => {
        const on = b.dataset.pick === what;
        b.classList.toggle('on', on);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
    $('#tsPickHint').textContent = what === 'white' ? T.hint_white : T.hint_tooth;
}

function onCanvasClick(e) {
    if (!state.img) return;
    const r = state.canvas.getBoundingClientRect();
    const x = Math.round((e.clientX - r.left) * (state.canvas.width / r.width));
    const y = Math.round((e.clientY - r.top) * (state.canvas.height / r.height));

    const sample = samplePatch(x, y, 11);
    if (state.picking === 'white') {
        state.white = sample;
        state.whiteAt = { x, y };
        // Подсказать следующий шаг, а не ждать, что человек догадается.
        if (!state.tooth) setPicking('tooth');
    } else {
        state.tooth = sample;
        state.toothAt = { x, y };
    }

    drawImage();
    renderResult();
}

/**
 * Средний цвет квадрата и доля выбитых пикселей.
 * Медиана по каналу, а не среднее: блик на эмали — это несколько очень
 * ярких пикселей, и среднее они утаскивают целиком.
 */
function samplePatch(x, y, size) {
    const half = size >> 1;
    const x0 = Math.max(0, x - half), y0 = Math.max(0, y - half);
    const w = Math.min(size, state.canvas.width - x0);
    const h = Math.min(size, state.canvas.height - y0);
    if (w <= 0 || h <= 0) return null;

    const d = state.ctx.getImageData(x0, y0, w, h).data;
    const R = [], G = [], B = [];
    let clipped = 0;
    for (let i = 0; i < d.length; i += 4) {
        R.push(d[i]); G.push(d[i + 1]); B.push(d[i + 2]);
        if (d[i] >= 250 || d[i + 1] >= 250 || d[i + 2] >= 250) clipped++;
    }
    const med = arr => { arr.sort((a, b) => a - b); return arr[arr.length >> 1]; };
    return { r: med(R), g: med(G), b: med(B), clipped: clipped / (d.length / 4) };
}

// ── цвет ────────────────────────────────────────────────────────────────

function srgbToLinear(v) {
    const c = v / 255;
    return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
}

function rgbToLab(r, g, b) {
    const R = srgbToLinear(r), G = srgbToLinear(g), B = srgbToLinear(b);
    // sRGB → XYZ (D65)
    const X = R * 0.4124564 + G * 0.3575761 + B * 0.1804375;
    const Y = R * 0.2126729 + G * 0.7151522 + B * 0.0721750;
    const Z = R * 0.0193339 + G * 0.1191920 + B * 0.9503041;
    // XYZ → Lab (белая точка D65)
    const f = t => t > 0.008856 ? Math.cbrt(t) : (7.787 * t + 16 / 116);
    const fx = f(X / 0.95047), fy = f(Y / 1.00000), fz = f(Z / 1.08883);
    return [116 * fy - 16, 500 * (fx - fy), 200 * (fy - fz)];
}

/**
 * Приведение к эталону: von-Kries по каналам. Белый лист обязан стать
 * нейтральным серым той же светлоты — и тот же коэффициент применяется к
 * зубу. Это и есть вся калибровка.
 */
function balance(tooth, white) {
    const target = (white.r + white.g + white.b) / 3;
    return {
        r: clamp255(tooth.r * (target / Math.max(1, white.r))),
        g: clamp255(tooth.g * (target / Math.max(1, white.g))),
        b: clamp255(tooth.b * (target / Math.max(1, white.b))),
        // Светлоту зуба нормируем на светлоту бумаги: бумага — это ~95 % по
        // отражению, и без нормировки тёмный снимок даст тёмный оттенок.
        scale: 242 / Math.max(1, target),
    };
}

function clamp255(v) { return Math.max(0, Math.min(255, v)); }

function deltaE(a, b) {
    return Math.sqrt((a[0] - b[0]) ** 2 + (a[1] - b[1]) ** 2 + (a[2] - b[2]) ** 2);
}

/** Ближайшая пластина VITA + вторая по близости (для честности ответа). */
function matchShade(lab) {
    const scored = CFG.shades.map((s, i) => ({ i, shade: s.shade, d: deltaE(lab, s.lab) }));
    scored.sort((a, b) => a.d - b.d);
    return { best: scored[0], second: scored[1] };
}

// ── результат ───────────────────────────────────────────────────────────

function renderResult() {
    const box = $('#tsResult');
    const warn = $('#tsWarn');
    warn.hidden = true;
    $('#tsSave').disabled = true;

    if (!state.white || !state.tooth) {
        box.textContent = state.white ? T.need_tooth : T.need_white;
        box.className = 'ts-result ts-muted';
        return;
    }

    /*
     * ⚠️ Пересвеченный эталон — ОТКАЗ, а не оговорка. Выбитый в 255 белый
     * не содержит информации о балансе: всё, что ярче потолка шкалы,
     * выглядит одинаково. Показать «оттенок» по такому кадру значит выдать
     * случайное число за замер.
     */
    if (state.white.clipped > 0.15) {
        warn.hidden = false;
        warn.textContent = T.warn_clipped;
        box.textContent = T.cannot_measure;
        box.className = 'ts-result ts-bad';
        if (window.__aeDiagnostic) window.__aeDiagnostic({ issue: 'white_clipped' });
        return;
    }
    if (state.tooth.clipped > 0.35) {
        warn.hidden = false;
        warn.textContent = T.warn_tooth_clipped;
    }
    // Слишком тёмный эталон — вторая сторона той же беды.
    const wl = (state.white.r + state.white.g + state.white.b) / 3;
    if (wl < 80) {
        warn.hidden = false;
        warn.textContent = T.warn_dark;
    }

    const bal = balance(state.tooth, state.white);
    const lab = rgbToLab(
        clamp255(bal.r * bal.scale),
        clamp255(bal.g * bal.scale),
        clamp255(bal.b * bal.scale)
    );
    const { best, second } = matchShade(lab);

    state.current = { lab, shade: best.shade, index: best.i, dE: best.d };

    box.className = 'ts-result';
    box.textContent = '';

    const big = document.createElement('b');
    big.textContent = best.shade;
    const sub = document.createElement('span');
    // Близость двух пластин — не мелочь: если разрыв меньше единицы ΔE,
    // ответ «A2» и ответ «C1» одинаково обоснованы, и молчать об этом
    // значит продавать уверенность, которой нет.
    const closeCall = (second.d - best.d) < 1.5;
    if (closeCall && window.__aeFunnel) window.__aeFunnel('close_call', { a: best.shade, b: second.shade });
    sub.textContent = closeCall
        ? fmt(T.close_call, { a: best.shade, b: second.shade })
        : fmt(T.match_ok, { shade: best.shade, dE: best.d.toFixed(1) });

    const swatch = document.createElement('i');
    swatch.className = 'ts-swatch';
    swatch.style.background = `rgb(${Math.round(bal.r * bal.scale)},${Math.round(bal.g * bal.scale)},${Math.round(bal.b * bal.scale)})`;

    box.append(swatch, big, sub);
    $('#tsSave').disabled = false;

    if (window.__aeFunnel) window.__aeFunnel('measured', { shade: best.shade });
}

// ── журнал замеров ──────────────────────────────────────────────────────

function restore() {
    try { state.log = JSON.parse(localStorage.getItem(KEY) || '[]') || []; } catch (_) { state.log = []; }
}

function persist() {
    try { localStorage.setItem(KEY, JSON.stringify(state.log)); } catch (_) {}
}

function saveMeasure() {
    if (!state.current) return;
    state.log.push({
        at: Date.now(),
        shade: state.current.shade,
        index: state.current.index,
        L: Math.round(state.current.lab[0] * 10) / 10,
        note: $('#tsNote').value.trim(),
    });
    state.log.sort((a, b) => a.at - b.at);
    persist();
    renderLog();
    $('#tsNote').value = '';
    window.__toast && window.__toast(T.saved, 'success');
    if (window.__aeToolComplete) window.__aeToolComplete({ ok: 1, shade: state.current.shade });
}

function clearLog() {
    if (!confirm(T.clear_confirm)) return;
    state.log = [];
    persist();
    renderLog();
}

function renderLog() {
    const box = $('#tsLog');
    box.textContent = '';
    $('#tsLogEmpty').hidden = state.log.length > 0;
    $('#tsProgress').hidden = state.log.length < 2;
    $('#tsClear').hidden = state.log.length === 0;

    for (const m of state.log) {
        const row = document.createElement('div');
        row.className = 'ts-log-row';
        const d = document.createElement('span');
        d.textContent = new Date(m.at).toLocaleDateString(window.__LOCALE || 'ru',
            { day: 'numeric', month: 'short', year: 'numeric' });
        const s = document.createElement('b');
        s.textContent = m.shade;
        const n = document.createElement('i');
        n.textContent = m.note || '';
        row.append(d, s, n);
        box.appendChild(row);
    }

    if (state.log.length >= 2) {
        const first = state.log[0], last = state.log[state.log.length - 1];
        // Ступени шкалы, а не «стал белее»: шкала упорядочена по светлоте,
        // и сдвиг на N позиций — это то число, которым меряют отбеливание.
        const steps = first.index - last.index;
        $('#tsProgressText').textContent = steps > 0
            ? fmt(T.progress_lighter, { n: steps, from: first.shade, to: last.shade })
            : steps < 0
                ? fmt(T.progress_darker, { n: -steps, from: first.shade, to: last.shade })
                : fmt(T.progress_same, { shade: last.shade });
    }
}

function fmt(tpl, vars) {
    return String(tpl || '').replace(/:(\w+)/g, (m, k) => (k in vars ? String(vars[k]) : m));
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
else boot();
